import React, { useState, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ImageLoader } from '../ImageLoader';
import { ViewProduct, ViewProducts } from '../../../models/ViewProduct.model';
import { ProductHandle } from '../../../enums/ProductHandle.enum';
import { AssetPathFn } from '../../../models/CommonFunctions';
import { PageContext } from '../../../enums/PageContext.enum';
import { scrollToRef, iconAbbreviation, cleanPrice, productTitlePart1, productTitlePart2 } from '../../../util';
import { SubContext } from '../../../enums/SubContext.enum';
import { HoneyloveWindow } from '../../../models/HoneyloveWindow';
import { SelectionType } from '../../../enums/SelectionType.enum';
import { ProductCategory } from '../../../enums/ProductCategory.enum';
import { abTestState, assetPathFnState, pageContextState } from '../../../recoil/atoms';
import { HoneyloveAbTests } from '../../../models/HoneyloveAbTests';
import { productsState } from '../../../recoil/selectors';
import classNames from 'classnames';
import styles from './StyleSelector.module.scss';
import { DivButton } from '../../../root/components/DivButton';
import { useProductSelection } from '../../../hooks/useProductSelection';

interface Props {
  product: ViewProduct;
  productSectionRef?: React.RefObject<HTMLDivElement>;
}

declare const window: HoneyloveWindow;

export const StyleSelector: React.FC<Props> = ({ product, productSectionRef }) => {
  const products = useRecoilValue(productsState);
  const abTests = useRecoilValue(abTestState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const pageContext = useRecoilValue(pageContextState);
  const selectionType = SelectionType.ProductSection;
  const { setHandle, setSubContext } = useProductSelection(selectionType, '0');
  const [sortedProducts, setSortedProducts] = useState<ViewProduct[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  useEffect(() => {
    !showAll && setSortedProducts(orderedProducts(products, product, abTests, showAll));
  }, [products, product, abTests, showAll]);

  const selectStyleAndScroll = (handle: ProductHandle, subContext: SubContext) => {
    if (pageContext === PageContext.GiftCardPage) {
      window.location.href = `/products/${handle}`;
    } else {
      const offset = pageContext === PageContext.ProductPage ? -64 : 0;
      setHandle(handle);
      setSubContext(subContext);
      productSectionRef && scrollToRef(productSectionRef, offset);
    }
  };
  const showAllProducts = () => {
    setSortedProducts(orderedProducts(products, product, abTests, true));
    setShowAll(true);
  };

  return (
    <div className={styles.hlStyleSelector}>
      <div className={classNames(styles.hlMobileProductToggle, styles.hideDesktop)}>
        {mobileStyleLinks(product, sortedProducts, assetPathFn, abTests, selectStyleAndScroll)}
        {!showAll && (
          <DivButton
            className={classNames(styles.hlProductImageWrapper, styles.hlExpanded, styles.hlShowMore)}
            onClick={showAllProducts}
            label="Show all products"
          >
            <div className={styles.hlEllipsis}>&hellip;</div>
            <div className={styles.hlProductTitleBlock}>
              <span className={styles.hlProductName}>
                <em className={styles.hlProductFirstWord}>All</em> Products
              </span>
            </div>
          </DivButton>
        )}
      </div>
      {!isProductPage(pageContext) && (
        <div className={classNames(styles.hlDesktopProductToggle, styles.hideMobile)}>
          {desktopStyleLinks(product, sortedProducts, assetPathFn, selectStyleAndScroll)}
          {!showAll && (
            <DivButton className={classNames(styles.hlProductImageWrapper, styles.hlShowMore)} onClick={showAllProducts} label="Show all products">
              <div className={styles.hlEllipsis}>&hellip;</div>
              <div className={styles.hlProductTitleBlock}>
                <span className={styles.hlProductName}>
                  <em className={styles.hlProductFirstWord}>Show</em> All Products
                </span>
              </div>
            </DivButton>
          )}
        </div>
      )}
    </div>
  );
};

const isProductPage = (pageContext: PageContext | undefined) => pageContext === PageContext.ProductPage;

const sortProducts = (activeProduct: ViewProduct) => (a: ViewProduct) => {
  const isActiveA = a.handle === activeProduct.handle;
  return isActiveA ? -1 : 1;
};

const orderedProducts = (products: ViewProducts, activeProduct: ViewProduct, abTests?: HoneyloveAbTests, showAll?: boolean) => {
  const includedCollections =
    activeProduct.collection === 'collection_bodysuits' || activeProduct.collection === 'collection_tops'
      ? ['collection_tops', 'collection_bodysuits']
      : [activeProduct.collection];
  const excludedTypes = [ProductCategory.GiftCard, ProductCategory.AddOns];
  if (showAll) {
    // sort by price desc then move active collection to front
    return Object.values(products)
      .filter(({ type, hiddenInMenus }) => excludedTypes.indexOf(type) === -1 && !hiddenInMenus)
      .sort((a, b) => b.price - a.price)
      .sort((a, b) => {
        if (includedCollections.indexOf(a.collection) > -1 && includedCollections.indexOf(b.collection) === -1) {
          return -1;
        }
        if (includedCollections.indexOf(a.collection) > -1 && includedCollections.indexOf(b.collection) > -1) {
          return 0;
        }
        return 1;
      });
  }
  return Object.values(products)
    .filter(({ collection, type, hiddenInMenus }) => includedCollections.indexOf(collection) > -1 && excludedTypes.indexOf(type) === -1 && !hiddenInMenus)
    .sort((a, b) => b.price - a.price);
};

const mobileStyleLinks = (
  product: ViewProduct,
  products: ViewProduct[],
  assetPathFn: AssetPathFn,
  abTests: HoneyloveAbTests,
  selectStyleAndScroll: (handle: ProductHandle, subContext: SubContext) => void,
) => products.map((p) => mobileStyleLink(p, product.handle === p.handle, assetPathFn, abTests, selectStyleAndScroll));

const mobileStyleLink = (
  product: ViewProduct,
  active: boolean,
  assetPathFn: AssetPathFn,
  abTests: HoneyloveAbTests,
  selectStyleAndScroll: (handle: ProductHandle, subContext: SubContext) => void,
) => {
  const imageLink = linkImage(product, active, assetPathFn);
  const productTitle = product.title;

  return imageLink ? (
    <DivButton
      key={product.handle}
      className={classNames(styles.hlProductImageWrapper, { [styles.hlSelected]: active, [styles.hlExpanded]: active })}
      onClick={() => !active && selectStyleAndScroll(product.handle, SubContext.IconProductSelector)}
      label="Select style"
    >
      <ImageLoader className={styles.hlMobileProductImage} src={imageLink} alt="product" />
      {active && (
        <div className={styles.hlProductTitleBlock}>
          <span className={styles.hlProductName} style={{ display: 'flex', flexDirection: 'column' }}>
            <span>
              <em className={styles.hlProductFirstWord}>{productTitlePart1(productTitle)}</em>
            </span>
            <span className={styles.hlProductSecondWord}>
              {productTitlePart2(productTitle)}&nbsp;&nbsp;<span className={styles.hlProductCleanPrice}>{cleanPrice(product.displayPrice)}</span>
            </span>
          </span>
        </div>
      )}
    </DivButton>
  ) : (
    <React.Fragment key={product.handle} />
  );
};
const desktopStyleLinks = (
  product: ViewProduct,
  products: ViewProduct[],
  assetPathFn: AssetPathFn,
  selectStyleAndScroll: (handle: ProductHandle, subContext: SubContext) => void,
) => products.map((p) => desktopStyleLink(p, product.handle === p.handle, assetPathFn, selectStyleAndScroll));

const desktopStyleLink = (
  product: ViewProduct,
  active: boolean,
  assetPathFn: AssetPathFn,
  selectStyleAndScroll: (handle: ProductHandle, subContext: SubContext) => void,
) => {
  const activeImageLink = linkImage(product, true, assetPathFn);
  const inactiveImageLink = linkImage(product, false, assetPathFn);
  const productTitle = product.title;

  return activeImageLink && inactiveImageLink ? (
    <DivButton
      key={product.handle}
      className={classNames(styles.hlProductImageWrapper, { [styles.hlSelected]: active })}
      onClick={() => !active && selectStyleAndScroll(product.handle, SubContext.IconProductSelector)}
      label="Select style"
    >
      <ImageLoader className={classNames(styles.hlMobileProductImage, styles.hlImageActive)} src={activeImageLink} alt="style icon" />
      <ImageLoader className={classNames(styles.hlMobileProductImage, styles.hlImageNormal)} src={inactiveImageLink} alt="style icon" />
      <div className={styles.hlProductTitleBlock}>
        <span className={styles.hlProductName}>
          <em className={styles.hlProductFirstWord}>{productTitlePart1(productTitle)}</em> {productTitlePart2(productTitle)}
        </span>
        <span className={styles.hlProductDescription}>{product.description}</span>
      </div>
    </DivButton>
  ) : (
    <React.Fragment key={product.handle} />
  );
};

export const linkImage = (product: ViewProduct, active: boolean, assetPathFn: AssetPathFn): string | undefined => {
  const icoPath = iconAbbreviation(product);
  return icoPath ? assetPathFn(`ico-${icoPath}${activeModifier(active)}_150x.png`) : undefined;
};

const activeModifier = (active: boolean) => (active ? '-active' : '-normal');

export default StyleSelector;
